import { useNavigate } from 'react-router-dom';
import { Badge, Card, Flex, Group, Text, Box, ThemeIcon, Paper, Alert, Button } from '@mantine/core';
import { IconPackage, IconMapPin, IconScale, IconCalendar, IconAlertCircle } from '@tabler/icons-react';
import { memo, useState } from 'react';

import { PaymentService } from '../../types';
import { showNotification } from '@mantine/notifications';

// Memoized ShipmentCard for better performance
interface ShipmentCardProps {
    shipment: any;
    actionButton?: React.ReactNode;
}
const ShipmentCard = memo(({ shipment }: ShipmentCardProps) => {
    // Safety check to prevent errors with undefined shipment
    if (!shipment) {
        return (
            <Card p="md" radius="md" withBorder shadow="sm">
                <Alert color="red" title="Error" icon={<IconAlertCircle />}>
                    Failed to load shipment data
                </Alert>
            </Card>
        );
    }

    // Format date
    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString();
    };
    const navigate = useNavigate();

    // Get status badge color and variant
    const getStatusInfo = (status: typeof shipment.pay_state) => {
        switch (status) {
            case 'INITIATED':
            case 'QR_CODE_GENERATED':
                return { color: 'blue', variant: 'light', label: 'Unpaid' };
            case 'CONFIRMED':
                return { color: 'green', variant: 'light', label: 'shipped' };
            default:
                return { color: 'red', variant: 'light', label: 'Payment Failed' };
        }
    };

    const statusInfo = getStatusInfo(shipment.pay_state);

    // Handle keyboard navigation
    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            navigate(`/shipments/${shipment.id}`);
        }
    };

    // Handle click
    const handleClick = () => {
        navigate(`/shipments/${shipment.id}`);
    };

    // Removed unused retryPayment function and fixed payment logic in button handlers below.
    // Payment action handlers
    const handlePayNow = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (shipment.payUrl) {
            window.location.href = shipment.payUrl;
        } else {
            try {
                await retryPayment();
            } catch (error) {
                showNotification({
                    title: 'Error',
                    message: 'Failed to retry payment. Please try again later.',
                    color: 'red',
                });
            } finally {
                setRetryLoading(false);
            }
        }
    };

    const [retryLoading, setRetryLoading] = useState(false);
    const handleRetryPayment = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setRetryLoading(true);
        try {
            await retryPayment();
        } catch (error) {
            showNotification({
                title: 'Error',
                message: 'Failed to retry payment. Please try again later.',
                color: 'red',
            });
        } finally {
            setRetryLoading(false);
        }
    };

    const retryPayment = async () => {
        try {
            const response: any = await PaymentService.postPaymentCreate({
                packageId: Number(shipment.packId),
                packType: shipment.packType as any
            });
            if (response.success) {
                showNotification({
                    title: 'Success',
                    message: 'Payment retry successful! Redirecting to payment page...',
                    color: 'green',
                });
                window.location.href = response.data.paymentUrl; // Redirect to payment URL
            } else {
            }
        } finally {
            setRetryLoading(false);
        }
    };

    // Determine if card is interactive based on status
    const isInteractive = shipment.status !== 'processing';

    return (
        <Card
            p="md"
            radius="md"
            withBorder
            shadow="sm"
            style={{
                cursor: 'pointer',
            }}
            bg={isInteractive ? 'white' : 'gray.1'}
            onClick={handleClick}
            onKeyDown={isInteractive ? handleKeyDown : undefined}
            tabIndex={isInteractive ? 0 : -1}
            role={isInteractive ? "button" : "region"}
            aria-label={isInteractive
                ? `View shipment details for ${shipment.name}`
                : `Shipment ${shipment.name} is being processed and cannot be viewed yet`
            }
        >
            {/* Status Badge - Positioned at top right */}
            <Badge
                color={statusInfo.color}
                variant={statusInfo.variant}
                size="sm"
                radius="sm"
                px="md"
                style={{ position: 'absolute', top: '12px', right: '12px' }}
            >
                {statusInfo.label}
            </Badge>

            {/* Processing notice - only shown when status is processing */}
            {/* {!isInteractive && (
                <Badge
                    color="gray"
                    variant="outline"
                    size="sm"
                    radius="sm"
                    px="md"
                    style={{ position: 'absolute', bottom: '12px', right: '12px' }}
                >
                    Not available yet
                </Badge>
            )} */}

            {/* Main Content */}
            <Flex direction="column" gap="md">
                {/* Header with Title */}
                <Text size="lg" fw={700} lineClamp={2} style={{ paddingRight: '70px' }}>
                    {shipment.name}
                </Text>

                {/* ID and Package Count */}
                <Group gap="xs">
                    <Text size="xs" c="dimmed">ID: {shipment.id}</Text>
                    <Text size="xs" c="dimmed">•</Text>
                    <Flex align="center" gap={4}>
                        <IconPackage size={14} style={{ color: 'var(--mantine-color-dimmed)' }} />
                        <Text size="xs" c="dimmed">{shipment.packageCount} package(s)</Text>
                    </Flex>
                </Group>

                {/* Shipment Details */}
                <Paper withBorder p="xs" radius="sm" bg="gray.0">
                    <Flex wrap="wrap" gap="md">
                        <Flex align="center" gap="xs" style={{ minWidth: '120px' }}>
                            <ThemeIcon color="blue" variant="light" size="sm" radius="xl">
                                <IconCalendar size={14} />
                            </ThemeIcon>
                            <Box>
                                <Text size="xs" fw={500} c="dimmed">Created</Text>
                                <Text size="sm">{formatDate(shipment.createdDate)}</Text>
                            </Box>
                        </Flex>

                        <Flex align="center" gap="xs" style={{ minWidth: '140px' }}>
                            <ThemeIcon color="indigo" variant="light" size="sm" radius="xl">
                                <IconMapPin size={14} />
                            </ThemeIcon>
                            <Box>
                                <Text size="xs" fw={500} c="dimmed">Destination</Text>
                                <Text size="sm">{shipment.destination.city}, {shipment.destination.country}</Text>
                            </Box>
                        </Flex>

                        <Flex align="center" gap="xs" style={{ minWidth: '100px' }}>
                            <ThemeIcon color="cyan" variant="light" size="sm" radius="xl">
                                <IconScale size={14} />
                            </ThemeIcon>
                            <Box>
                                <Text size="xs" fw={500} c="dimmed">Weight</Text>
                                <Text size="sm">{shipment.totalWeight} kg</Text>
                            </Box>
                        </Flex>
                    </Flex>
                </Paper>

                {/* Footer with Cost and Action Button */}
                <Flex justify="space-between" align="center" mt="xs">
                    <Box>
                        <Text size="xs" fw={500} c="dimmed">Total Cost</Text>
                        <Text size="md" fw={700} c="blue.7">
                            {typeof shipment.cost === 'object' && shipment.cost !== null && 'total' in shipment.cost
                                ? `$${shipment.cost.total.toFixed(2)}`
                                : typeof shipment.cost === 'number'
                                    ? `$${shipment.cost.toFixed(2)}`
                                    : '$0.00'}
                        </Text>
                    </Box>

                    {/* Payment State Actions */}
                    {(shipment.pay_state === 'INITIATED' || shipment.pay_state === 'QR_CODE_GENERATED') && (
                        <Button color="blue" variant="filled" onClick={handlePayNow}>Pay Now</Button>
                    )}
                    {shipment.pay_state !== 'INITIATED' && shipment.pay_state !== 'CONFIRMED' && shipment.pay_state !== 'QR_CODE_GENERATED' && (
                        <Group gap="xs">
                            <Button color="red" variant="outline" onClick={handleRetryPayment} loading={retryLoading}>
                                Retry Payment
                            </Button>
                        </Group>
                    )}
                </Flex>
            </Flex>
        </Card>
    );
});

export default ShipmentCard;
